import { LocalizedString } from "../../../services/redux/types/CartLoyaltyV2Types";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import {
  PromoAction,
  PromoActionType,
  PromoTextContent,
} from "../../../utils/promoMaterial/promoMaterialTypes";
import iconLayout from "../GeneralOfferIcon/GeneralOfferIcon.module.scss";
import { AnnouncementIcon } from "../Icons/loyalty.icons";
import styles from "./PromoAnnouncementBanner.module.scss";
import { PromoBannerActionButton, PromoBannerCard } from "./PromoBannerCard";

/**
 * An admin-authored banner with no offer behind it: the campaign's own words,
 * badged with the announcement icon rather than an offer's motif — nothing about
 * it is a reward, so borrowing the reward iconography would misread.
 *
 * Only the button is tappable, as on the offer banner. A campaign that authored
 * no CTA, or whose action this build cannot honour, simply has no button — the
 * card is then a notice, which is a legitimate thing for a campaign to publish.
 */
export const PromoAnnouncementBanner = ({
  content,
  action,
  onAction,
  tierSized,
}: {
  content: PromoTextContent;
  action?: PromoAction;
  onAction: (action?: PromoAction) => void;
  tierSized?: boolean;
}) => {
  // The same reader the announcement pop-up uses: full locale, then its base
  // language, then English.
  const localized = (value?: LocalizedString) =>
    Offer.getLocalizedValue(value) ?? "";

  const headline = localized(content.headline);
  const description = localized(content.description);
  const buttonText = localized(content.buttonText);
  const hasAction = action && action.type !== PromoActionType.NONE;

  return (
    <PromoBannerCard
      testId="promo_announcement_banner"
      tierSized={tierSized}
      icon={
        // Borrows the offer icon's own slider geometry (`.slider`/`.offer_label`
        // position and size the badge, and hang it over the card's leading
        // edge), so the announcement badge sits exactly where an offer's motif
        // would. Only the paint below is ours.
        <div className={iconLayout.slider}>
          <div className={iconLayout.offer_label}>
            <span
              className={styles.icon_disc}
              data-testid="promo_announcement_banner-icon"
            >
              <AnnouncementIcon />
            </span>
          </div>
        </div>
      }
      title={headline}
      description={description || undefined}
      action={
        hasAction && buttonText ? (
          <PromoBannerActionButton
            onClick={() => onAction(action)}
            testId="promo_announcement_banner-cta"
          >
            {buttonText}
          </PromoBannerActionButton>
        ) : undefined
      }
    />
  );
};
